'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Mail, CheckCircle, Clock, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface EmailVerificationMessageProps {
  email: string
  onResendVerification?: () => void
  isResending?: boolean
}

const EmailVerificationMessage: React.FC<EmailVerificationMessageProps> = ({
  email,
  onResendVerification,
  isResending = false
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 24,
        padding: '36px 32px',
        maxWidth: 480,
        width: '100%',
        margin: '0 auto',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
        textAlign: 'center'
      }}
    >
      <div>
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          style={{ width: 72, height: 72, margin: '0 auto 20px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.4))', border: '1px solid rgba(16,185,129,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 30px rgba(16,185,129,0.3)' }}
        >
          <CheckCircle className="w-9 h-9 text-emerald-400" />
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{ fontSize: 24, fontWeight: 900, color: '#ffffff', margin: '0 0 10px', letterSpacing: '-0.02em', fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Account Created!
        </motion.h2>

        {/* Email Icon and Address */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', margin: '0 auto 20px' }}
        >
          <Mail className="w-4 h-4 text-emerald-400" />
          <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>{email}</span>
        </motion.div>

        {/* Verification Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div style={{ padding: '16px 20px', borderRadius: 16, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.20)', display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left' }}>
            <Clock className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 style={{ fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#6ee7b7', margin: '0 0 4px' }}>
                Check Your Email
              </h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, margin: 0 }}>
                We&apos;ve sent a verification link to your email address.
                Click the link in the message to activate your account.
              </p>
            </div>
          </div>

          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <p style={{ margin: 0 }}>📧 Check your inbox (and spam folder)</p>
            <p style={{ margin: 0 }}>🔗 Click the verification link in the email</p>
            <p style={{ margin: 0 }}>✅ Return here to sign in</p>
          </div>

          {/* Resend Button */}
          {onResendVerification && (
            <div style={{ marginTop: 8 }}>
              <Button
                onClick={onResendVerification}
                disabled={isResending}
                variant="outline"
                style={{ width: '100%', height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#ffffff', fontWeight: 700, fontSize: 14 }}
              >
                {isResending ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Mail className="w-4 h-4" />
                    <span>Resend Verification Email</span>
                  </div>
                )}
              </Button>
            </div>
          )}

          <div style={{ paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
              Didn&apos;t receive the email? Check your spam folder or try resending.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default EmailVerificationMessage


